<template>
  <div
    v-if="dsComponentDetails.version === VersionOptions.LEGACY && hasAppendOrPrependTextOrShowPassword"
    class="input-container"
    :class="state"
    data-sb-regression="sb-text-input"
    @click="focusInput"
  >
    <span
      v-if="hasPrependText"
      class="prepend"
    >
      <!--
          @slot text to prepend to the input element
        -->
      <slot name="prepend">
        {{ prepend }}
      </slot>
    </span>
    <input
      :id="internalId"
      v-bind="attrs"
      ref="legacyRef"
      :class="classes"
      :placeholder="placeholder"
      :type="showPassword && showingPassword ? 'text' : type"
      :maxlength="maxLengthValue"
      v-on="attrs"
      @input="inputChanged"
      @focus="handleFocus"
      @blur="handleBlur"
    >
    <span
      v-if="hasAppendText"
      class="append"
    >
      <!--
          @slot text to append to the input element
        -->
      <slot name="append">
        {{ append }}
      </slot>
    </span>
    <span
      v-else-if="showPassword"
      class="show-password"
      @click="showingPassword = !showingPassword"
    >
      <img
        v-if="showingPassword"
        alt="preview"
        :src="previewHiddenIcon"
      >
      <img
        v-else
        alt="preview"
        :src="previewIcon"
      >
    </span>
  </div>
  <input
    v-else-if="dsComponentDetails.version === VersionOptions.LEGACY && !hasAppendOrPrependTextOrShowPassword"
    :id="internalId"
    v-bind="attrs"
    ref="legacyRef"
    :class="classes"
    :placeholder="placeholder"
    :type="type"
    :maxlength="maxLengthValue"
    v-on="attrs"
    @input="inputChanged"
    @focus="handleFocus"
    @blur="handleBlur"
  >
  <SbTextInputNext
    v-else-if="dsComponentDetails.version === VersionOptions.NEXT"
    :id="internalId"
    v-bind="attrs"
    ref="nextRef"
    :class="classes"
    :placeholder="placeholder"
    :show-password="showPassword"
    :type="type"
    :maxlength="maxLengthValue"
    v-on="attrs"
    @input="inputChanged"
    @focus="handleFocus"
    @blur="handleBlur"
  >
    <template
      v-if="hasPrependText"
      #prepend
    >
      <slot name="prepend">
        {{ prepend }}
      </slot>
    </template>
    <template
      v-if="hasAppendText"
      #append
    >
      <slot name="append">
        {{ append }}
      </slot>
    </template>
  </SbTextInputNext>
</template>

<script>
import labelHelper from '@src/utils/mixins/labelHelperMixin';
import fieldHelper from '@src/utils/mixins/fieldHelperMixin';
import textFieldHelper from '@src/utils/mixins/textFieldHelperMixin';
import previewIcon from '@public/media/icons/preview.svg';
import previewHiddenIcon from '@public/media/icons/preview-hidden.svg';
import { versionedComponentsMixin, VersionOptions } from '@src/mixins/versionedComponents';
import SbTextInputNext from '@src/components/Input/SbTextInputNext.vue';

/**
 * Text Inputs allows users to input text. :)
 */
export default {
  name: 'SbTextInput',
  components: {
    SbTextInputNext,
  },
  mixins: [labelHelper, fieldHelper, textFieldHelper, versionedComponentsMixin],
  props: {
    /**
     * String to be used as the input placeholder text.
     */
    placeholder: {
      type: String,
      default: '',
    },
    /**
     * String to be appended to the input box.
     * This prop text is actually used as the default value of a named slot: `append`.
     * If for some reason you have some special html markup you would like to append to the input box in leiu of a simple `span` with text,
     * eg. `<p slot="append">my appended text</p>`.
     * you can use the named slot.
     */
    append: {
      type: String,
      default: '',
    },
    /**
     * String to be prepended to the input box.
     * This prop text is actually used as the default value of a named slot: `prepend`.
     * If for some reason you have some special html markup you would like to append to the input box in leiu of a simple `span` with text,
     * eg. `<p slot="prepend">my prepended text</p>`.
     * you can use the named slot.
     */
    prepend: {
      type: String,
      default: '',
    },
    /**
     * Text alignment of the input box. Can be `left|center|right`
     */
    align: {
      type: String,
      default: 'left',
      validator: (value) => {
        return value.match(/(left|center|right)/);
      },
    },
    /**
     * Type of text input. Can be `text|tel|email|url|search|password`
     */
    type: {
      type: String,
      default: 'text',
      validator: (value) => {
        return value.match(/(text|tel|email|url|search|password|number)/);
      },
    },
    /**
     * html id of the input element. default will be set as the Vue component uuid.
     */
    id: {
      type: String,
      default: null,
    },
    value: {
      type: String,
      required: false,
      default: null,
    },
    /**
     * If you are using this input as part of the SbField pattern with SbFieldHelperText, counter is used to set the upper limit of the Field.
     * *This does not actually set a maxlength property on the input. It is only used as a visual inside the SbFieldHelperText.*
     */
    counter: {
      type: Number,
      default: null,
    },
    /**
     * Sets the max possible length on the text input. If you are using this input as part of the SbField pattern with SbFieldHelperText, maxlength is used to set the upper limit of the Field and appear in SbFieldHelperText.
     */
    maxlength: {
      type: Number,
      default: null,
    },
    /**
     * Manually trigger various states of the button.
     * `hover, active, focus`
     */
    state: {
      type: String,
      default: null,
      validator: (value) => value.match(/(focus)/),
    },
    /**
     * To enable the Show and Hide Password
     * Only works when type is set to password
     */
    showPassword: {
      type: Boolean,
      default: false,
    },
  },
  expose: ['focus', 'blur'],
  data() {
    return {
      showingPassword: false,
      previewIcon,
      previewHiddenIcon,
    };
  },
  computed: {
    hasAppendText() {
      return this.append.length > 0 || (this.$slots.append && this.$slots.append());
    },
    hasPrependText() {
      return this.prepend.length > 0 || (this.$slots.prepend && this.$slots.prepend());
    },
    hasAppendOrPrependTextOrShowPassword() {
      return this.hasAppendText || this.hasPrependText || (this.type === 'password' && this.showPassword);
    },
    classes() {
      const classList = ['sb-input'];
      if (this.align !== 'left') classList.push(`sb-input-${this.align}`);
      if (this.state !== null) classList.push(this.state);
      return classList;
    },
  },
  methods: {
    focus() {
      if (this.dsComponentDetails.version === VersionOptions.LEGACY) {
        this.$refs.legacyRef?.focus();
      } else {
        this.$refs.nextRef?.focus();
      }
    },
    blur() {
      if (this.dsComponentDetails.version === VersionOptions.LEGACY) {
        this.$refs.legacyRef?.blur();
      } else {
        this.$refs.nextRef?.blur();
      }
    },
  },
};
</script>

<style lang="scss" scoped>
@import '@src/components/Input/style/sb-text-input.scss';
</style>
